import {createRouter, createWebHistory} from 'vue-router';
import {useAuthStore} from '@/stores/use-auth-store';

import Login from '@/modules/auth/views/Login.vue';
import MainLayout from '@/layouts/MainLayout.vue';

// Lazy-loaded views — each import maps to its domain module
const Home = () => import('@/modules/dashboard/views/Home.vue');
const Flags = () => import('@/modules/submission/views/Flags.vue');
const Form = () => import('@/modules/submission/views/Form.vue');
const Pay = () => import('@/modules/pay/views/Pay.vue');
const MarketProgress = () => import('@/modules/market/views/MarketProgress.vue');
const OpsCollectors = () => import('@/modules/ops/views/OpsCollectors.vue');
const OpsMarkets = () => import('@/modules/ops/views/OpsMarkets.vue');
const OpsWindows = () => import('./modules/ops/views/OpsSubmissionWindows.vue');
const OpsFlags = () => import('./modules/ops/views/OpsSubmissionFlags.vue');
const MarketOutputMenu = () => import('@/modules/market/views/MarketOutputMenu.vue');
const MarketOutput = () => import('@/modules/market/views/MarketOutput.vue');
const Help = () => import('@/modules/shared/views/Help.vue');
const ReportIssue = () => import('@/modules/support/views/ReportIssue.vue');
const AdminPointer = () => import('@/modules/shared/views/AdminPointer.vue');

const routes = [
    {
        path: '/login',
        name: 'login',
        component: Login,
        meta: {guest: true},
    },

    // All authenticated screens share the MainLayout shell
    {
        path: '/',
        component: MainLayout,
        meta: {auth: true},
        children: [
            {path: '', name: 'home', component: Home},
            {path: 'help', name: 'help', component: Help},
            {path: 'report-issue', name: 'report-issue', component: ReportIssue},
            {path: 'admin-pointer', name: 'admin-pointer', component: AdminPointer},

            // Field roles (+ admin for support access)
            {path: 'flags', name: 'flags', component: Flags, meta: {roles: ['isFieldRole', 'isAdmin']}},
            {path: 'pay', name: 'pay', component: Pay, meta: {roles: ['isFieldRole', 'isAdmin', 'isFinanceRole', 'isCommercialRole']}},
            {path: 'market/:uuid', name: 'market', component: MarketProgress, meta: {roles: ['isFieldRole', 'isAdmin', 'isOpsRole', 'isCommercialRole']}},
            {
                path: 'form/:type/:assignmentId',
                name: 'form',
                component: Form,
                meta: {roles: ['isFieldRole', 'isAdmin']}
            },

            // Ops roles only
            {path: 'ops/collectors', name: 'ops-collectors', component: OpsCollectors, meta: {roles: ['isOpsRole']}},
            {path: 'ops/markets', name: 'ops-markets', component: OpsMarkets, meta: {roles: ['isOpsRole']}},
            {path: 'ops/windows', name: 'ops-windows', component: OpsWindows, meta: {roles: ['isOpsRole']}},
            {path: 'ops/flags', name: 'ops-flags', component: OpsFlags, meta: {roles: ['isOpsRole']}},
            {path: 'output', name: 'market-output-menu', component: MarketOutputMenu, meta: {roles: ['isOpsRole']}},
            {path: '/output/:uuid', name: 'market-output', component: MarketOutput, meta: {roles: ['isOpsRole']}},
        ],
    },

    {path: '/:pathMatch(.*)*', redirect: {name: 'home'}},
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: () => ({top: 0}),
});

// Navigation guard — restore session on first load, then gate by auth and role
router.beforeEach(async (to) => {
    const auth = useAuthStore();

    if (!auth.user && to.meta.auth) {
        try {
            await auth.fetchMe();
        } catch {
            return {name: 'login', query: {redirect: to.fullPath}};
        }
    }

    if (to.meta.guest && auth.isAuthenticated) {
        return {name: 'home'};
    }

    // Role guard: meta.roles lists auth store getters; user must satisfy at least one
    if (to.meta.roles && auth.user) {
        const allowed = to.meta.roles.some(getter => auth[getter]);
        if (!allowed) return {name: 'home'};
    }

    return true;
});

// When a lazy-loaded chunk fails (slow / offline mobile connection), force a
// full page reload to that path so the browser retries the fetch or the SW
// serves the cached chunk. Without this, the navigation silently aborts.
router.onError((error, to) => {
    const isChunkError =
        /failed to fetch|importing a module script failed|dynamically imported module|loading chunk/i
            .test(error?.message || '');
    if (isChunkError) {
        window.location.assign(to.fullPath);
    }
});

export default router;
